<template>
    <div>
        <page-header
            :page="pageConfig"
            has-return
            :has-save="showSaveButton"
            @onReturn="onReturn"
            @onSave="saveQuestionaries"
            right-side-class="col-sm-12 col-md-4 d-flex justify-content-end align-items-center actions text-right"
        />

        <Error
          is-alert-mode
          message="Los equipos que están en buenas condiciones y que, por alguna razón discutida en COMPU escolar, no continúen utilizándose por el estudiante, deben devolverse al Centro de Servicios de Educación Especial (CSEE) de su área geográfica."
        />

        <SectionTitle
            :has-line="true"
            content="Equipos asignados al estudiante"
        />

        <CustomCollapse id="collapse-equipament-questionary">
            <ValidationObserver
                v-for="(serviceQuestionary, i) in servicesQuestions"
                :key="`${serviceQuestionary.serviceRecommendationId}-${i}`"
                :ref="observerRef(serviceQuestionary.serviceRecommendationId)"
                v-slot="{ invalid, validated }"
            >
                <CustomCollapseItem
                    class="collapse-item__container"
                    :title="serviceQuestionary.equipmentName"
                    :id="`service-equipament-questionary-${i}`"
                    parent-id="collapse-equipament-questionary"
                    :error="validated && invalid"
                    :checked="
                        isQuestionaryComplete(serviceQuestionary) ||
                        (validated && !invalid)
                    "
                    customErrorMsg="Favor completar los campos requeridos"
                >
                    <span
                        v-for="questionItem in serviceQuestionary.questions"
                        :key="`${serviceQuestionary.serviceRecommendationId}-${questionItem.id}`"
                    >
                        <FrequencyQuestion
                            v-if="questionItem.id == QuestionEnum.Frequency"
                            v-model="questionItem.answer"
                            :disabled="isReadonly"
                            :question="questionItem.question"
                            class="question__container"
                        />
                        <SchoolIntegrationQuestion
                            v-if="questionItem.id == QuestionEnum.SchoolIntegration"
                            v-model="questionItem.answer"
                            :disabled="isReadonly"
                            :question="questionItem.question"
                            class="question__container"
                        />
                        <LocationQuestion
                            v-if="questionItem.id == QuestionEnum.Location"
                            v-model="questionItem.answer"
                            :disabled="isReadonly"
                            :question="questionItem.question"
                            class="question__container"
                        />
                        <ConditionQuestion
                            v-if="questionItem.id == QuestionEnum.Condition"
                            v-model="questionItem.answer"
                            :disabled="isReadonly"
                            :question="questionItem.question"
                            class="question__container"
                        />
                        <KeepNextYearQuestion
                            v-if="questionItem.id == QuestionEnum.KeepNextYear"
                            v-model="questionItem.answer"
                            :disabled="isReadonly"
                            :question="questionItem.question"
                            class="question__container"
                        />
                        <FromOtherSchoolQuestion
                            v-if="questionItem.id == QuestionEnum.FromOtherSchool"
                            v-model="questionItem.answer"
                            :disabled="isReadonly"
                            :question="questionItem.question"
                            class="question__container"
                        />
                        <KeepUsingQuestion
                            v-if="questionItem.id == QuestionEnum.KeepUsing"
                            v-model="questionItem.answer"
                            :disabled="isReadonly"
                            :question="questionItem.question"
                            class="question__container"
                        />
                    </span>
                </CustomCollapseItem>
            </ValidationObserver>
        </CustomCollapse>

        <TitleBreadcrumb
            :page="certificationTitle"
            :has-route="false"
            class="mt-5"
        />
        <CustomCheckboxSingleOption
          v-model="isAcepted"
          :disabled="isReadonly"
          title="Certifico que la información provista es correcta"
          option-text="Acepto"
        />        
    </div>
</template>
<script>
import PageHeader from "@/components/common/page/PageHeader";
import Error from "@/components/common/Error";
import alertMixin from "utils/mixins/alert.mixin";
import storeMixin from "utils/mixins/storeGenerator";
import SectionTitle from "@/components/pei/shared/SectionTitle";
import CustomCollapse from '@/components/common/CustomCollapse';
import CustomCollapseItem from '@/components/common/CustomCollapseItem';
import CustomCheckboxSingleOption from "custom-checkbox-single-option";
import FrequencyQuestion from "./questionary/FrequencyQuestion.vue";
import SchoolIntegrationQuestion from "./questionary/SchoolIntegrationQuestion.vue";
import LocationQuestion from "./questionary/LocationQuestion.vue";
import ConditionQuestion from "./questionary/ConditionQuestion.vue";
import KeepNextYearQuestion from "./questionary/KeepNextYearQuestion.vue";
import FromOtherSchoolQuestion from "./questionary/FromOtherSchoolQuestion.vue";
import KeepUsingQuestion from "./questionary/KeepUsingQuestion.vue";
import TitleBreadcrumb from "@/components/common/page/TitleBreadcrumb";
import QuestionEnum from "@/views/pei/atEquipment/common/QuestionEnum";
import { ValidationObserver } from "vee-validate";

// Answers that make the comment field required, by question type
const COMMENT_REQUIRED_ANSWERS = {
    [QuestionEnum.Frequency]: ['does-not-use-it'],
    [QuestionEnum.Location]: ['home'],
    [QuestionEnum.FromOtherSchool]: ['yes'],
    [QuestionEnum.KeepUsing]: ['yes'],
};

export default {  
    name: "EquipmentQuestionsView",
    mixins: [storeMixin, alertMixin],
    components: {
        Error,
        PageHeader,
        SectionTitle,
        CustomCollapse,
        CustomCollapseItem,
        FrequencyQuestion,
        SchoolIntegrationQuestion,
        LocationQuestion,
        ConditionQuestion,
        KeepNextYearQuestion,
        FromOtherSchoolQuestion,
        KeepUsingQuestion,
        CustomCheckboxSingleOption,
        TitleBreadcrumb,
        ValidationObserver
    },
    data: () => ({
        isAcepted: false,
        isReadonly: true,
        QuestionEnum: QuestionEnum,
        servicesQuestions: [],
        certificationTitle: {
            icon: "far fa-check-circle",
            title: "Certificación de Asistencia Tecnológica"
        },
    }),
    computed: {
        pageConfig() {
            return {
                icon: "fas fa-desktop",
                title: "Servicios y equipos de asistencia tecnológica (AT)",
                routes: [
                    "Inicio",
                    "PEI",
                    "Programa de Servicio"
                ]
            };
        },
        showSaveButton() {
            return !this.isReadonly && this.isAcepted;
        },
        observerRef() {
            return (serviceRecommendationId) => {
                return `questionary-${serviceRecommendationId}`;
            };
        },
        observerHasError() {
            return (serviceRecommendationId) => {
                return this.$refs[this.observerRef(serviceRecommendationId)][0].hasError();
            };
        }
    },
    methods: {
        onReturn() {
            this.$router.push('/pei/detail?tab=Tab7');
        },
        getQuestionAnswers(question) {
            // Answers may be plain strings from the API or objects
            // emitted by the question components after an edit
            const raw = question.answer;
            if (raw !== null && typeof raw === 'object') {
                return { answer: raw.answer, comment: raw.comment };
            }
            return { answer: raw, comment: question.comment };
        },
        commentIsRequired(questionId, answer) {
            const requiredValues = COMMENT_REQUIRED_ANSWERS[questionId];
            if (!requiredValues) return false;
            const selected = String(answer)
                .split(',')
                .map((option) => option.trim());
            return requiredValues.some((value) => selected.includes(value));
        },
        isQuestionaryComplete(serviceQuestionary) {
            return serviceQuestionary.questions.every((question) => {
                const { answer, comment } = this.getQuestionAnswers(question);
                const hasAnswer = !!answer && String(answer).trim() !== '';
                if (!hasAnswer) return false;
                if (!this.commentIsRequired(question.id, answer)) return true;
                return !!comment && String(comment).trim() !== '';
            });
        },
        async fetchQuestionaries() {
            var { data } = await this.$ApiGet(`api/atservice/equipment/questionary/${this.$route.params.peiId}`);
            this.isReadonly = data.isReadonly;
            this.servicesQuestions = [...data.questionaries];
        },
        async saveQuestionaries() {
            let isValid = true;
            for (let serviceQuestionary of this.servicesQuestions) {
                const result = await this.$refs[this.observerRef(serviceQuestionary.serviceRecommendationId)][0].validate();
                isValid = isValid && result;
            }
            
            if (!isValid) return;

            try {
                await this.$ApiPut(`api/atservice/equipment/questionary`, {
                    peiId: this.$route.params.peiId,
                    questionaries: this.servicesQuestions
                });

                this.ShowToast(
                    'Proceso exitoso.',
                    'Cuestionario guardado exitosamente',
                    'success'
                );
            } catch (err) {
                this.ShowToast(
                    'Error',
                    `Error al guardar el cuestionario.`,
                    'error'
                );
            }
        }
    },
    watch: {
        isReadonly: {
            immediate: true,
            handler(newValue) {
                this.isAcepted = newValue;
            }
        },
    },
    async mounted() {
        await this.fetchQuestionaries();
    },
};
</script>

<style scoped>
.question__container {
    padding-top: 30px;
    border-bottom: 1px solid #C3C3C3;
}

.collapse-item__container {
    margin: 1rem 0;
    background-color: #F7F7F7;
}

::v-deep .card-pei {
    width: auto;
}
</style>
  